import React, { useState, useEffect, useCallback } from 'react';
import {
  PartyPopper, Plus, ChevronDown, CalendarDays, Clock,
  MapPinned, Users, DollarSign, Info, Eye, Edit, Trash2,
  AlertCircle, CheckCircle, X, Loader2, Save, MapPin, ImagePlus, Image
} from 'lucide-react';

const BusinessEventsSection = ({
  events,
  searchedBusiness,
  businessEventContext,
  adminId,
  createTracker,
  onEventsUpdate,
  eventImagesMap = {},
  onManageEventImages
}) => {
  // Alias context
  const businessEvent = businessEventContext;

  // ============================================================
  // LOCAL STATE
  // ============================================================
  const [eventFilter, setEventFilter] = useState('next_30_days');
  const [filteredEvents, setFilteredEvents] = useState([]);
  
  // Modal & Form State
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventModalMode, setEventModalMode] = useState('create'); // 'create' | 'edit'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDeleteConfirm, setShowEventDeleteConfirm] = useState(null);
  
  // Feedback State
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false);
  const [eventError, setEventError] = useState(null);
  const [eventSuccess, setEventSuccess] = useState(null);

  // Form Data
  const [eventFormData, setEventFormData] = useState({
    event_title: '',
    event_date: '',
    event_time: '',
    event_description: '',
    event_status: 'scheduled',
    event_guest_limit: '',
    event_cost: '',
    event_price: '',
    event_location: '',
    event_location_latitude: '',
    event_location_longitude: '',
    event_instructions: '',
    location_id: '',
    client_id: '',
    employee_id: '',
    product_id: '',
    service_id: '',
    event_visible: true,
  });

  // ============================================================
  // EFFECTS & FILTERS
  // ============================================================

  // Auto-clear messages
  useEffect(() => {
    if (eventError) {
      const timer = setTimeout(() => setEventError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [eventError]);

  useEffect(() => {
    if (eventSuccess) {
      const timer = setTimeout(() => setEventSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [eventSuccess]);

  // Filtering Logic
  const filterEvents = useCallback((eventList, filterType) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (filterType) {
      case 'all_future':
        return eventList.filter(event => new Date(event.event_date) >= today);
      case 'next_6_months': {
        const sixMonthsFromNow = new Date(today);
        sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
        return eventList.filter(event => {
          const eventDate = new Date(event.event_date);
          return eventDate >= today && eventDate <= sixMonthsFromNow;
        });
      }
      case 'next_30_days': {
        const thirtyDaysFromNow = new Date(today);
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
        return eventList.filter(event => {
          const eventDate = new Date(event.event_date);
          return eventDate >= today && eventDate <= thirtyDaysFromNow;
        });
      }
      case 'next_7_days': {
        const sevenDaysFromNow = new Date(today);
        sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
        return eventList.filter(event => {
          const eventDate = new Date(event.event_date);
          return eventDate >= today && eventDate <= sevenDaysFromNow;
        });
      }
      case 'past_30_days': {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return eventList.filter(event => {
          const eventDate = new Date(event.event_date);
          return eventDate < today && eventDate >= thirtyDaysAgo;
        });
      }
      case 'past_6_months': {
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        return eventList.filter(event => {
          const eventDate = new Date(event.event_date);
          return eventDate < today && eventDate >= sixMonthsAgo;
        });
      }
      case 'all_past':
        return eventList.filter(event => new Date(event.event_date) < today);
      case 'all_events':
      default:
        return eventList;
    }
  }, []);

  // Apply Filter
  useEffect(() => {
    if (events && events.length > 0) {
      const filtered = filterEvents(events, eventFilter);
      setFilteredEvents(filtered);
    } else {
      setFilteredEvents([]);
    }
  }, [events, eventFilter, filterEvents]);

  const getFilterLabel = (filterType) => {
    const labels = {
      'all_future': 'Todos los Futuros',
      'next_6_months': 'Próximos 6 Meses',
      'next_30_days': 'Próximos 30 Días',
      'next_7_days': 'Próximos 7 Días',
      'all_events': 'Todos los Eventos',
      'past_30_days': 'Últimos 30 Días',
      'past_6_months': 'Últimos 6 Meses',
      'all_past': 'Todos los Pasados',
    };
    return labels[filterType] || filterType;
  };

  // ============================================================
  // FORM HANDLERS
  // ============================================================

  const handleCreateEvent = () => {
    setEventModalMode('create');
    setSelectedEvent(null);
    setEventFormData({
      event_title: '',
      event_date: '',
      event_time: '',
      event_description: '',
      event_status: 'scheduled',
      event_guest_limit: '',
      event_cost: '',
      event_price: '',
      event_location: '',
      event_location_latitude: '',
      event_location_longitude: '',
      event_instructions: '',
      location_id: '',
      client_id: '',
      employee_id: '',
      product_id: '',
      service_id: '',
      event_visible: true,
    });
    setEventError(null);
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    setEventModalMode('edit');
    setSelectedEvent(event);
    setEventFormData({
      event_title: event.event_title || '',
      event_date: event.event_date ? event.event_date.split('T')[0] : '',
      event_time: event.event_time || '',
      event_description: event.event_description || '',
      event_status: event.event_status || 'scheduled',
      event_guest_limit: event.event_guest_limit || '',
      event_cost: event.event_cost || '',
      event_price: event.event_price || '',
      event_location: event.event_location || '',
      event_location_latitude: event.event_location_latitude || '',
      event_location_longitude: event.event_location_longitude || '',
      event_instructions: event.event_instructions || '',
      location_id: event.location_id || '',
      client_id: event.client_id || '',
      employee_id: event.employee_id || '',
      product_id: event.product_id || '',
      service_id: event.service_id || '',
      event_visible: event.event_visible !== undefined ? event.event_visible : true,
    });
    setEventError(null);
    setShowEventModal(true);
  };

  const handleEventInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEventFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    if (!searchedBusiness) return;

    setIsSubmittingEvent(true);
    setEventError(null);

    try {
      const submitData = {
        ...eventFormData,
        event_time: eventFormData.event_time.length === 5
          ? `${eventFormData.event_time}:00`
          : eventFormData.event_time,
        event_description: eventFormData.event_description || null,
        event_guest_limit: eventFormData.event_guest_limit ? parseInt(eventFormData.event_guest_limit) : null,
        event_cost: eventFormData.event_cost ? parseFloat(eventFormData.event_cost) : null,
        event_price: eventFormData.event_price ? parseFloat(eventFormData.event_price) : null,
        event_location: eventFormData.event_location || null,
        event_location_latitude: eventFormData.event_location_latitude ? parseFloat(eventFormData.event_location_latitude) : null,
        event_location_longitude: eventFormData.event_location_longitude ? parseFloat(eventFormData.event_location_longitude) : null,
        event_instructions: eventFormData.event_instructions || null,
        location_id: eventFormData.location_id || null,
        client_id: eventFormData.client_id || null,
        employee_id: eventFormData.employee_id || null,
        product_id: eventFormData.product_id || null,
        service_id: eventFormData.service_id || null,
      };

      if (eventModalMode === 'create') {
        submitData.business_registry_number = searchedBusiness.business_registry_number;
        await businessEvent.createEvent(submitData);
        setEventSuccess('Evento creado exitosamente');
        
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'event_create',
          change_reason: `Event created for ${searchedBusiness.business_registry_number}. Event: ${submitData.event_title}`
        });
      } else {
        await businessEvent.updateEvent(selectedEvent.event_id, submitData);
        setEventSuccess('Evento actualizado exitosamente');

        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'event_update',
          change_reason: `Event updated for ${searchedBusiness.business_registry_number}. Event ID: ${selectedEvent.event_id}`
        });
      }

      if (onEventsUpdate) await onEventsUpdate();
      setShowEventModal(false);
      setSelectedEvent(null);

    } catch (error) {
      console.error('Error submitting event:', error);
      setEventError(error.message || 'Error al guardar el evento');
    } finally {
      setIsSubmittingEvent(false);
    }
  };

  // ============================================================
  // ACTION HANDLERS
  // ============================================================

  const handleDeleteEvent = async (eventId) => {
    if (!searchedBusiness) return;
    const eventToDelete = events.find(e => e.event_id === eventId);

    try {
      await businessEvent.deleteEvent(eventId);
      setEventSuccess('Evento eliminado exitosamente');
      setShowEventDeleteConfirm(null);

      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'event_delete',
        change_reason: `Event deleted for ${searchedBusiness.business_registry_number}. ID: ${eventId}, Title: ${eventToDelete?.event_title}`
      });

      if (onEventsUpdate) await onEventsUpdate();
    } catch (error) {
      setEventError(error.message || 'Error al eliminar evento');
    }
  };

  const handleQuickStatusUpdate = async (eventId, newStatus) => {
    try {
      await businessEvent.updateEventStatus(eventId, newStatus);
      setEventSuccess(`Estado actualizado a: ${newStatus}`);
      
      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'event_status_change',
        change_reason: `Event status changed. ID: ${eventId}, New status: ${newStatus}`
      });

      if (onEventsUpdate) await onEventsUpdate();
    } catch (error) {
      setEventError(error.message || 'Error al actualizar estado');
    }
  };

  const handleToggleVisibility = async (eventId, currentVisibility) => {
    try {
      await businessEvent.updateEventVisibility(eventId, !currentVisibility);
      setEventSuccess(`Visibilidad ${!currentVisibility ? 'activada' : 'desactivada'}`);
      if (onEventsUpdate) await onEventsUpdate();
    } catch (error) {
      setEventError(error.message || 'Error al cambiar visibilidad');
    }
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-orange-200 p-6">
      
      {/* Header & Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <PartyPopper className="h-5 w-5 text-orange-500" />
          Eventos de Negocio
        </h3>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          {/* Filter Dropdown */}
          <div className="relative">
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 appearance-none cursor-pointer"
            >
              <optgroup label="Eventos Futuros">
                <option value="next_7_days">Próximos 7 Días</option>
                <option value="next_30_days">Próximos 30 Días</option>
                <option value="next_6_months">Próximos 6 Meses</option>
                <option value="all_future">Todos los Futuros</option>
              </optgroup>
              <optgroup label="Todos">
                <option value="all_events">Todos los Eventos</option>
              </optgroup>
              <optgroup label="Eventos Pasados">
                <option value="past_30_days">Últimos 30 Días</option>
                <option value="past_6_months">Últimos 6 Meses</option>
                <option value="all_past">Todos los Pasados</option>
              </optgroup>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>

          <button
            onClick={handleCreateEvent}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            Crear Evento
          </button>
        </div>
      </div>

      {/* Global Feedback */}
      {eventSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <span className="text-green-700">{eventSuccess}</span>
        </div>
      )}
      {eventError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{eventError}</span>
        </div>
      )}

      {/* Filter Stats */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-600">
          Mostrando: <span className="font-medium text-gray-800">{getFilterLabel(eventFilter)}</span>
        </span>
        <span className="text-gray-400">•</span>
        <span className="text-gray-600">
          <span className="font-medium text-orange-600">{filteredEvents.length}</span> de{' '}
          <span className="font-medium text-gray-800">{events.length}</span> eventos
        </span>
      </div>

      {/* Events List */}
      {filteredEvents.length > 0 ? (
        <div className="space-y-4">
          {filteredEvents.map((event) => {
            const formatted = businessEvent.formatEventData(event);
            const primaryImage = eventImagesMap[event.event_id];
            const hasImage = !!primaryImage;

            return (
              <div key={event.event_id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all">
                <div className="flex flex-col md:flex-row">
                  {/* Event Image */}
                  <div className="md:w-48 h-48 md:h-auto bg-gray-100 flex-shrink-0 relative group">
                    {hasImage ? (
                      <>
                        <img
                          src={primaryImage.cloudflare_image_url}
                          alt={event.event_title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                          <button
                            onClick={() => onManageEventImages && onManageEventImages(event)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity px-4 py-2 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-medium flex items-center gap-2"
                          >
                            <ImagePlus className="h-4 w-4" />
                            Gestionar
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        onClick={() => onManageEventImages && onManageEventImages(event)}
                        className="w-full h-full flex flex-col items-center justify-center text-gray-400 hover:text-orange-500 hover:bg-gray-50 transition-colors"
                      >
                        <Image className="h-12 w-12 mb-2" />
                        <span className="text-sm font-medium">Agregar Imagen</span>
                      </button>
                    )}
                  </div>

                  {/* Event Content */}
                  <div className="flex-1 p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-800 text-lg">{event.event_title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{event.event_description || 'Sin descripción'}</p>
                          </div>

                          <div className="flex items-center gap-2 ml-4">
                            <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                              formatted.statusColor === 'blue' ? 'bg-blue-100 text-blue-700' :
                              formatted.statusColor === 'green' ? 'bg-green-100 text-green-700' :
                              formatted.statusColor === 'red' ? 'bg-red-100 text-red-700' :
                              formatted.statusColor === 'orange' ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {event.event_status}
                            </span>
                            {formatted.isPastEvent ? (
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Pasado</span>
                            ) : (
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Próximo</span>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 flex-shrink-0" />
                            <span>{formatted.formattedDateTime}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 flex-shrink-0" />
                            <span className={formatted.isPastEvent ? 'text-gray-400' : 'text-orange-600 font-medium'}>
                              {formatted.durationFromNow}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPinned className="h-4 w-4 flex-shrink-0" />
                            <span className="truncate" title={formatted.locationText}>{formatted.locationText}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 flex-shrink-0" />
                            <span>{formatted.guestLimitText}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 flex-shrink-0" />
                            <span>{formatted.priceDisplay}</span>
                          </div>
                          {event.event_cost && event.event_price && (
                            <div className="flex items-center gap-2">
                              <Info className="h-4 w-4 flex-shrink-0" />
                              <span>Margen: {formatted.profitMargin}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => onManageEventImages && onManageEventImages(event)}
                          className="p-2 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors"
                          title="Gestionar imágenes"
                        >
                          <ImagePlus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleVisibility(event.event_id, event.event_visible)}
                          className="p-2 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg transition-colors"
                          title={event.event_visible ? 'Ocultar' : 'Mostrar'}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowEventDeleteConfirm(event.event_id)}
                          className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Quick Actions for Scheduled Events */}
                    {event.event_status === 'scheduled' && (
                      <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                        {formatted.isPastEvent && (
                          <div className="w-full text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded mb-2 flex items-center gap-2">
                            <AlertCircle className="h-3 w-3" />
                            Evento programado en fecha pasada
                          </div>
                        )}
                        <button onClick={() => handleQuickStatusUpdate(event.event_id, 'completed')} className="text-xs px-3 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded transition-colors">Completar</button>
                        <button onClick={() => handleQuickStatusUpdate(event.event_id, 'cancelled')} className="text-xs px-3 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded transition-colors">Cancelar</button>
                        {!formatted.isPastEvent && (
                          <button onClick={() => handleQuickStatusUpdate(event.event_id, 'postponed')} className="text-xs px-3 py-1 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded transition-colors">Posponer</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <PartyPopper className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No se encontraron eventos con el filtro seleccionado.</p>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: CREATE / EDIT */}
      {/* ============================================================ */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEventModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-800">
                {eventModalMode === 'create' ? 'Crear Nuevo Evento' : 'Editar Evento'}
              </h3>
              <button onClick={() => setShowEventModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <form id="event-form" onSubmit={handleSubmitEvent} className="space-y-6">
                
                {/* Info */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">Información Básica</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                    <input type="text" name="event_title" value={eventFormData.event_title} onChange={handleEventInputChange} required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                      <input type="date" name="event_date" value={eventFormData.event_date} onChange={handleEventInputChange} required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hora *</label>
                      <input type="time" name="event_time" value={eventFormData.event_time} onChange={handleEventInputChange} required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                    <textarea name="event_description" value={eventFormData.event_description} onChange={handleEventInputChange} rows="3" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 resize-none" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Estado *</label>
                      <select name="event_status" value={eventFormData.event_status} onChange={handleEventInputChange} required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500">
                        <option value="scheduled">Programado</option>
                        <option value="completed">Completado</option>
                        <option value="cancelled">Cancelado</option>
                        <option value="postponed">Pospuesto</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Límite Invitados</label>
                      <input type="number" name="event_guest_limit" value={eventFormData.event_guest_limit} onChange={handleEventInputChange} min="0" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" placeholder="Sin límite" />
                    </div>
                  </div>
                </div>

                {/* Financials */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">Precios</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Costo</label>
                      <input type="number" name="event_cost" value={eventFormData.event_cost} onChange={handleEventInputChange} min="0" step="0.01" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Precio</label>
                      <input type="number" name="event_price" value={eventFormData.event_price} onChange={handleEventInputChange} min="0" step="0.01" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" placeholder="0.00" />
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">Ubicación</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                    <input type="text" name="event_location" value={eventFormData.event_location} onChange={handleEventInputChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Latitud</label>
                      <input type="number" name="event_location_latitude" value={eventFormData.event_location_latitude} onChange={handleEventInputChange} step="any" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Longitud</label>
                      <input type="number" name="event_location_longitude" value={eventFormData.event_location_longitude} onChange={handleEventInputChange} step="any" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                    </div>
                  </div>
                </div>

                {/* Advanced / Optional */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">Opcional</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ID Ubicación</label>
                      <input type="text" name="location_id" value={eventFormData.location_id} onChange={handleEventInputChange} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ID Cliente</label>
                      <input type="text" name="client_id" value={eventFormData.client_id} onChange={handleEventInputChange} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <input type="checkbox" name="event_visible" id="event_visible" checked={eventFormData.event_visible} onChange={handleEventInputChange} className="h-4 w-4 text-orange-600 rounded" />
                    <label htmlFor="event_visible" className="text-sm font-medium text-gray-700">Evento visible para el público</label>
                  </div>
                </div>
              </form>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button type="button" onClick={() => setShowEventModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white">Cancelar</button>
              <button type="submit" form="event-form" disabled={isSubmittingEvent} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50">
                {isSubmittingEvent ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {eventModalMode === 'create' ? 'Crear' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: DELETE CONFIRM */}
      {/* ============================================================ */}
      {showEventDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEventDeleteConfirm(null)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full"><Trash2 className="h-6 w-6 text-red-600" /></div>
              <h3 className="text-lg font-semibold text-gray-800">Eliminar Evento</h3>
            </div>
            <p className="text-gray-600 mb-6">¿Estás seguro? Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowEventDeleteConfirm(null)} className="px-4 py-2 border rounded-lg text-gray-700">Cancelar</button>
              <button onClick={() => handleDeleteEvent(showEventDeleteConfirm)} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">Eliminar</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BusinessEventsSection;